import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  matchPath,
  useHistory
} from 'react-router-dom';
import Footer from './components/footer/Footer';
import Navbar from './components/navbar/Navbar';
import Notifications from './components/notifications/Notifications';
import About from './pages/about/About';
import ChangePassword from './pages/changePassword/ChangePassword';
import Contact from './pages/contact/Contact';
import Home from './pages/home/Home';
import LoggedOutHome from './pages/home/loggedOutHome/LoggedOutHome';
import Login from './pages/login/Login';
import Navigates from './pages/navigate/Navigate';
import PaymentError from './pages/pricing/PaymentError';
import PaymentSuccess from './pages/pricing/PaymentSuccess';
import Pricing from './pages/pricing/Pricing';
import Programs from './pages/programs/Programs';
import ProgramsIndex from './pages/programs/ProgramsIndex';
import ResetPassword from './pages/resetPassword/ResetPassword';
import Signup from './pages/signup/Signup';
import Verify from './pages/verify/Verify';
import VerifyResult from './pages/verify/VerifyResult';
import LocationHistoryMapModal from './pages/verify/LocationHistoryMapModal';
import IssuerMiddlemenList from './pages/home/certificateIssuerHome/IssuerMiddlemenList';
import IssuerCreateMiddleman from './pages/home/certificateIssuerHome/IssuerCreateMiddleman';
import { hideNavbarAndFooter, showNavbarAndFooter } from './slice/appSlice';
import { AccountTypeEnum } from './utils/enums';

import './App.css';

function App() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const isLoggedIn = useSelector(state => state.app.isLoggedIn);
  const accountType = useSelector(state => state.app.accountType);
  const shouldShowNavbarAndFooter = useSelector(
    state => state.app.showNavbarAndFooter
  );

  useEffect(() => {
    const isMatchPath = matchPath(location.pathname, {
      path: '/programs-index/:_id',
      exact: true,
      strict: true
    });

    if (isMatchPath) {
      dispatch(hideNavbarAndFooter());
    } else {
      dispatch(showNavbarAndFooter());
    }
  }, [location, dispatch]);

  useEffect(() => {
    const handleAuthExpired = () => {
      localStorage.clear();
      sessionStorage.clear();
      history.replace('/login');
    };

    window.addEventListener('auth-expired', handleAuthExpired);
    return () =>
      window.removeEventListener('auth-expired', handleAuthExpired);
  }, [history]);

  useEffect(() => {
    const revalidateSession = async () => {
      if (!isLoggedIn) return;

      try {
        const res = await fetch('/auth/me', {
          credentials: 'include',
          cache: 'no-store',
        });

        if (res.status === 401 || res.status === 403) {
          window.dispatchEvent(new Event('auth-expired'));
        }
      } catch {
        // Ignore network errors
      }
    };

    window.addEventListener('focus', revalidateSession);
    return () =>
      window.removeEventListener('focus', revalidateSession);
  }, [isLoggedIn]);

  [
    ['Verdana', 'url(https://fonts.gstatic.com/l/font?kit=dFa_ZfqA86A4lLhf7qRHfw&skey=28f652d19e80fbde&v=v14)'],
    ['Courier New', 'url(https://fonts.gstatic.com/l/font?kit=ijwRs57lRMAkZYJiwPkexnNGfJ4&skey=e2fc0b95199b08bc&v=v18)'],
    ['Montserrat', 'url(https://fonts.gstatic.com/s/montserrat/v23/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCtr6Hw5aXo.woff2)'],
    ['Noto Serif', 'url(https://fonts.gstatic.com/s/notoserif/v20/ga6Iaw1J5X9T9RW6j9bNfFcWaA.woff2)'],
    ['Cambay', 'url(https://fonts.gstatic.com/s/cambay/v10/SLXJc1rY6H0_ZDs2ab6J.woff2)'],
    ['Fira Sans', 'url(https://fonts.gstatic.com/s/firasans/v15/va9E4kDNxMZdWfMOD5Vvl4jL.woff2)'],
    ['Open Sans', 'url(https://fonts.gstatic.com/s/opensans/v28/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgsjZ0B4gaVI.woff2)'],
    ['Roboto', 'url(https://fonts.gstatic.com/s/roboto/v29/KFOmCnqEu92Fr1Mu4mxK.woff2)'],
    ['Lato', 'url(https://fonts.gstatic.com/s/lato/v22/S6uyw4BMUTPHjx4wXg.woff2)']
  ].forEach(font => {
    const fontFace = new FontFace(font[0], font[1]);
    fontFace.load().then(font => {
      document.fonts.add(font);
    });
  });

  return (
    <div className="App">
      <Notifications />
      {shouldShowNavbarAndFooter && <Navbar isLoggedIn={isLoggedIn} />}

      <Switch>
        <Route exact path="/" component={Home} />
        <Route exact path="/index" component={LoggedOutHome} />
        <Route exact path="/programs" component={Programs} />
        <Route exact path="/programs-index/:_id" component={ProgramsIndex} />

        {!isLoggedIn && <Route exact path="/login" component={Login} />}
        {!isLoggedIn && <Route exact path="/signup" component={Signup} />}
        {!isLoggedIn && (
          <Route exact path="/reset-password" component={ResetPassword} />
        )}

        {isLoggedIn && (
          <Route exact path="/change-password" component={ChangePassword} />
        )}

        <Route exact path="/pricing" component={Pricing} />

        {isLoggedIn &&
          accountType === AccountTypeEnum.CERTIFICATE_ISSUER && (
            <Route exact path="/payment-success" component={PaymentSuccess} />
          )}

        {isLoggedIn &&
          accountType === AccountTypeEnum.CERTIFICATE_ISSUER && (
            <Route exact path="/payment-error" component={PaymentError} />
          )}

        <Route exact path="/contact" component={Contact} />
        <Route exact path="/verify" component={Verify} />
        <Route exact path="/verify-result" component={VerifyResult} />
        <Route exact path="/location-history" component={LocationHistoryMapModal} />

        {isLoggedIn &&
          accountType === AccountTypeEnum.CERTIFICATE_ISSUER && (
            <Route exact path="/issuer/accounts" component={IssuerMiddlemenList} />
          )}

        {isLoggedIn &&
          accountType === AccountTypeEnum.CERTIFICATE_ISSUER && (
            <Route
              exact
              path="/issuer/accounts/create"
              component={IssuerCreateMiddleman}
            />
          )}

        <Route exact path="/navigate" component={Navigates} />
        <Route exact path="/about" component={About} />

        <Route path="*" render={() => <Redirect to="/" />} />
      </Switch>

      {shouldShowNavbarAndFooter && <Footer isLoggedIn={isLoggedIn} />}
    </div>
  );
}

export default App;
